import React from 'react';
import { MerchantInvoice, MerchantBillingSettings } from '../../../types/gst';
import { ShieldCheck, QrCode, Sparkles } from 'lucide-react';

interface Props {
  invoice: MerchantInvoice;
  settings?: MerchantBillingSettings;
}

export const ObsidianSignatureTemplate: React.FC<Props> = ({ invoice, settings }) => {
  const sellerName = invoice.sellerTradeName || invoice.sellerLegalName || settings?.companyName || 'Verve AI Labs';
  const sellerGstin = invoice.sellerGstin || settings?.gstin || '';
  const sellerAddress = invoice.sellerAddress || settings?.address || '';
  const upiId = settings?.upiId || 'verveailabs@hdfcbank';

  return (
    <div className="bg-[#0b0c0e] text-[#f7f7f8] p-8 font-sans max-w-4xl mx-auto border border-cyan-500/30 shadow-[0_12px_40px_rgba(6,182,212,0.12)] rounded-3xl text-xs selection:bg-cyan-500/30 print:bg-white print:text-black print:border-none print:shadow-none print:p-0">
      {/* Header */}
      <div className="border-b border-white/10 pb-6 mb-6 flex justify-between items-start">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-cyan-400 animate-pulse"></span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 font-bold">Verve AI Signature Invoice</span>
          </div>
          <h1 className="text-2xl font-black text-white tracking-tight uppercase">{sellerName}</h1>
          <p className="text-slate-400 max-w-md text-[11px] leading-relaxed">{sellerAddress}</p>
          <div className="flex items-center gap-4 text-slate-300 font-mono text-[11px] pt-1">
            {sellerGstin && <span>GSTIN: <strong className="text-cyan-300">{sellerGstin}</strong></span>}
            <span>State: {invoice.sellerStateCode}</span>
          </div>
        </div>

        <div className="text-right space-y-1">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-cyan-500/40 bg-cyan-950/40 px-3 py-1 text-cyan-300 font-mono font-bold text-xs uppercase tracking-wider">
            <Sparkles className="h-3 w-3" />
            <span>TAX INVOICE</span>
          </div>
          <div className="font-mono text-sm font-bold text-white pt-1">#{invoice.invoiceNumber}</div>
          <div className="text-slate-400 text-[11px]">Date: {invoice.invoiceDate}</div>
          {invoice.dueDate && <div className="text-slate-400 text-[11px]">Due: {invoice.dueDate}</div>}
        </div>
      </div>

      {/* Recipient & Compliance Meta */}
      <div className="grid grid-cols-2 gap-4 rounded-2xl border border-white/10 bg-[#12141a]/90 p-4 mb-6">
        <div>
          <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Billed To</div>
          <div className="text-base font-bold text-white mt-0.5">{invoice.customerName}</div>
          <div className="text-slate-400 text-[11px] mt-0.5">{invoice.customerBillingAddress || 'Address on record'}</div>
          {invoice.customerGstin && (
            <div className="mt-1.5 font-mono text-xs">
              <span className="text-slate-500">GSTIN: </span>
              <span className="text-cyan-300 font-bold">{invoice.customerGstin}</span>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center pl-4 border-l border-white/10">
          <div className="space-y-1 text-[11px]">
            <div><span className="text-slate-500">Place of Supply:</span> <span className="font-semibold text-slate-200">{invoice.placeOfSupply}</span></div>
            <div><span className="text-slate-500">Supply Regime:</span> <span className="font-semibold text-cyan-400">{invoice.isInterState ? 'Inter-State (IGST)' : 'Intra-State (CGST + SGST)'}</span></div>
            {invoice.eInvoice?.irn && (
              <div className="font-mono text-[9px] text-slate-400 truncate max-w-[220px]">
                IRN: {invoice.eInvoice.irn.substring(0, 20)}...
              </div>
            )}
          </div>
          {upiId && (
            <div className="rounded-xl border border-cyan-500/30 bg-[#090b0e] p-2 text-center shrink-0">
              <QrCode className="h-10 w-10 text-cyan-400 mx-auto" />
              <div className="text-[8px] font-mono text-slate-400 mt-0.5">UPI Pay</div>
            </div>
          )}
        </div>
      </div>

      {/* Line Items Table */}
      <div className="rounded-2xl border border-white/10 overflow-hidden mb-6">
        <table className="w-full text-left">
          <thead className="bg-[#14161f] text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-white/10">
            <tr>
              <th className="py-3 px-4">Item & Description</th>
              <th className="py-3 px-2 text-center font-mono">HSN/SAC</th>
              <th className="py-3 px-2 text-right">Qty</th>
              <th className="py-3 px-2 text-right">Unit Price</th>
              <th className="py-3 px-2 text-right">Taxable</th>
              <th className="py-3 px-2 text-right">GST</th>
              <th className="py-3 px-4 text-right">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 font-mono text-slate-300 text-xs">
            {invoice.lineItems.map((item, idx) => (
              <tr key={idx} className="hover:bg-white/[0.02]">
                <td className="py-3 px-4 font-sans font-medium text-white">
                  {item.description}
                  {item.discountAmount > 0 && (
                    <div className="text-[10px] text-emerald-400 font-mono">Discount: -₹{item.discountAmount.toFixed(2)}</div>
                  )}
                </td>
                <td className="py-3 px-2 text-center text-slate-400">{item.hsnSac || '—'}</td>
                <td className="py-3 px-2 text-right">{item.quantity} {item.unit}</td>
                <td className="py-3 px-2 text-right">₹{item.unitPrice.toLocaleString('en-IN')}</td>
                <td className="py-3 px-2 text-right">₹{item.taxableAmount.toLocaleString('en-IN')}</td>
                <td className="py-3 px-2 text-right text-cyan-400 font-bold">{item.gstRate}%</td>
                <td className="py-3 px-4 text-right font-bold text-white">₹{item.totalAmount.toLocaleString('en-IN')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bottom Summary */}
      <div className="grid grid-cols-2 gap-6 pt-2">
        <div className="rounded-2xl border border-white/10 bg-[#12141a]/90 p-4 space-y-2 text-[11px]">
          <div className="font-bold text-cyan-300 flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" />
            <span>Bank & Payment Routing</span>
          </div>
          <div>Bank: <strong className="text-white">{settings?.bankName || 'HDFC Bank Ltd'}</strong></div>
          <div>A/C No: <strong className="font-mono text-white">{settings?.accountNumber || '50200088991122'}</strong></div>
          <div>IFSC: <strong className="font-mono text-white">{settings?.ifscCode || 'HDFC0000240'}</strong></div>
          <div>UPI ID: <strong className="font-mono text-cyan-300">{upiId}</strong></div>
          <div className="pt-2 text-[10px] text-slate-500 whitespace-pre-line leading-relaxed border-t border-white/5">
            {invoice.termsAndConditions || settings?.defaultTerms}
          </div>
        </div>

        <div className="space-y-2 text-right">
          <div className="flex justify-between text-slate-400 py-0.5">
            <span>Taxable Subtotal:</span>
            <span className="font-mono font-medium text-white">₹{invoice.subtotalTaxable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          {invoice.isInterState ? (
            <div className="flex justify-between text-cyan-400 py-0.5">
              <span>Integrated Tax (IGST):</span>
              <span className="font-mono font-bold">₹{invoice.totalIgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
          ) : (
            <>
              <div className="flex justify-between text-slate-300 py-0.5">
                <span>Central Tax (CGST):</span>
                <span className="font-mono">₹{invoice.totalCgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-slate-300 py-0.5">
                <span>State Tax (SGST):</span>
                <span className="font-mono">₹{invoice.totalSgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
            </>
          )}
          {invoice.roundOff !== 0 && (
            <div className="flex justify-between text-slate-500 py-0.5">
              <span>Round Off:</span>
              <span className="font-mono">₹{invoice.roundOff.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between border-t border-cyan-500/40 pt-2 font-bold text-base text-white">
            <span>Invoice Total:</span>
            <span className="font-mono text-xl text-cyan-300 font-black">₹{invoice.grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          {invoice.balanceDue > 0 ? (
            <div className="flex justify-between text-rose-400 font-semibold pt-1">
              <span>Balance Due:</span>
              <span className="font-mono">₹{invoice.balanceDue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
          ) : (
            <div className="text-emerald-400 font-bold text-right pt-1">PAID IN FULL ✅</div>
          )}

          <div className="pt-8">
            <div className="font-bold text-white">{sellerName}</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">Statutory Authorized Signatory</div>
          </div>
        </div>
      </div>
    </div>
  );
};
